<script setup lang="ts">
import { useFetch } from '@/composables/useFetch.js';
import { useOrder } from '@/composables/useOrders';
import { useEnvStore } from '@/stores/envStore.js';
import { useUserStore } from '@/stores/useUserStore';
import type { Order, TableRow } from '@/types/order.js';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import LoadingView from '../LoadingView.vue';

const { fetchData } = useFetch();
const apiUrl = useEnvStore().apiUrl;
const userStore = useUserStore();
const route = useRoute();
const { t } = useI18n();
const toast = useToast();
const { generatePDF } = useOrder();

const order = ref<Order | null>(null);
const isLoading = ref(true);
const restaurantId = userStore.user?.restaurants[0].id;

async function fetchOrderDetails() {
  if (!restaurantId) return;

  const orderId = route.params.orderId;
  try {
    const { data, error } = await fetchData(`${apiUrl}/restaurants/${restaurantId}/orders/${orderId}`, 'GET');
    if (error) {
      console.error('Error fetching order details:', error);
    } else {
      order.value = data as Order;
    }
  } catch (e) {
    console.error('Exception while fetching order details:', e);
  } finally {
    isLoading.value = false;
  }
}

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

function formatOrderForClipboard() {
  if (!order.value) return '';
  const date = formatDate(new Date(order.value.wanted_date));
  // Use optional chaining with fallback text for supplier
  const supplierName = order.value.supplier ? order.value.supplier.name : t('orders.noSupplier');
  let text = `${t('orders.orderForm.file.orderOfDate', { date: formatDate(new Date()) })} - ${userStore.user?.restaurants[0].name}\n\n`;
  text += `${t('orders.orderForm.file.provider')}: ${supplierName}\n`;
  text += `${t('orders.orderForm.file.deliveryDateWanted')}: ${date}\n\n`;
  text += `${t('orders.orderForm.file.ingredientsList')}:\n`;
  order.value.ingredients.forEach((ingredient) => {
    text += `- ${ingredient.quantity} ${ingredient.unit} ${ingredient.name}\n`;
  });
  if (order.value.custom_message) {
    text += `\n${t('orders.orderForm.file.customMessage')}:\n${order.value.custom_message}`;
  }
  text += `\nRestaurant: \n- ${userStore.user?.restaurants[0].name}\n- ${userStore.user?.restaurants[0].phone_number}\n`;
  return text;
}

async function copyOrderToClipboard() {
  const text = formatOrderForClipboard();
  try {
    await navigator.clipboard.writeText(text);
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('orders.orderForm.copy.success'),
      life: 3000,
    });
  } catch (err) {
    console.error('Failed to copy order to clipboard:', err);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('orders.orderForm.copy.error'),
      life: 3000,
    });
  }
}

async function handleGeneratePDF() {
  if (!order.value) return;

  const ingredients: TableRow[] = order.value.ingredients.map((ing) => ({
    type: 'ingredient',
    data: ing,
  }));
  const supplier = order.value.supplier;
  await generatePDF(
    t,
    ingredients,
    supplier ? [supplier] : [],
    supplier ? supplier.uuid : null,
    new Date(order.value.wanted_date),
    order.value.custom_message,
  );
}

onMounted(() => {
  fetchOrderDetails();
});
</script>

<template>
  <main class="p-4">
    <LoadingView v-if="isLoading" />
    <div v-else-if="order" class="space-y-4">
      <h1 class="mb-4 font-bold text-2xl">{{ t('orders.orderNb', { orderNb: order.id }) }}</h1>

      <div>
        <h3 class="font-semibold">{{ t('orders.orderCreatedAt') }}</h3>
        <p>{{ formatDate(new Date(order.created_at)) }}</p>
      </div>

      <div>
        <h3 class="font-semibold">{{ t('orders.emailStatus.label') }}</h3>
        <p>
          {{ order.email_sent ? t('orders.emailStatus.sent') : t('orders.emailStatus.notSent') }}
        </p>
      </div>

      <div>
        <h3 class="font-semibold">{{ t('orders.orderForm.supplier') }}</h3>
        <p>{{ order.supplier ? order.supplier.name : t('orders.noSupplier') }}</p>
      </div>

      <div>
        <h3 class="font-semibold">{{ t('orders.orderForm.wantedDate') }}</h3>
        <p>{{ formatDate(new Date(order.wanted_date)) }}</p>
      </div>

      <div v-if="order.custom_message">
        <h3 class="font-semibold">{{ t('orders.orderForm.customMessage') }}</h3>
        <p>{{ order.custom_message }}</p>
      </div>

      <div>
        <h3 class="font-semibold">{{ t('orders.orderForm.shippingList') }}</h3>
        <DataTable :value="order.ingredients">
          <Column field="quantity" :header="t('orders.orderForm.quantityShort')" />
          <Column field="unit" :header="t('orders.orderForm.unit')" />
          <Column field="name" :header="t('orders.orderForm.name')" />
        </DataTable>
      </div>

      <div class="flex gap-2">
        <Button icon="pi pi-copy" :tooltip="t('orders.orderForm.copy.tooltip')" class="p-button-outlined" @click="copyOrderToClipboard" />
        <Button
          icon="pi pi-file-pdf"
          :tooltip="t('orders.orderForm.pdf.tooltip')"
          class="p-button-outlined p-button-secondary"
          @click="handleGeneratePDF"
        />
      </div>
    </div>
  </main>
</template>
